import { Button, ISearchFilterRef } from '@/components';
import {
	PageTypeEnum,
	TableSettingColumnSubTypeMap,
	TableTypes,
	UserSettingTypeEnum,
} from '@/constant';
import { useMergingState } from '@/hooks';
import { TGetListUserSettingResponse } from '@/services';
import { CommonActions, CommonSelectors } from '@/stores';
import { convertCamelToSnakeCaseWithoutTableName, map } from '@/utils';
import { SettingOutlined } from '@ant-design/icons';
import { memo, RefObject, useCallback, useEffect, useMemo } from 'react';
import {
	DragDropContext,
	Draggable,
	Droppable,
	DropResult,
	ResponderProvided,
} from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Checkbox, GetProp, Popover, TabPaneProps, Tabs } from 'antd';
import { CheckboxOptionType } from 'antd/es/checkbox';

import '../style.scss';
import { clone, length, uniq } from 'ramda';
import { isArray } from 'ramda-adjunct';

export interface TTabItem extends Omit<TabPaneProps, 'tab' | 'options'> {
	key: string;
	label: React.ReactNode;
	options: (CheckboxOptionType & { fixed?: boolean })[];
	checkedList?: string[];
	ids?: string[];
	sort?: (a: string, b: string) => number;
}

export interface TSettingColumn {
	tableType?: TableTypes;
	isSettingCol?: boolean;
	tabItems?: TTabItem[];
	mergeMultiKeyCols?: {
		mergedKey: string;
		childKeys: string[];
	}[];
	onSave?: (
		settingListFields: TGetListUserSettingResponse,
		tabActive?: string
	) => void;
	searchFilterRef?: RefObject<ISearchFilterRef>;
	rowKey?: string;
}

interface TState {
	open: boolean;
	tabItems: TTabItem[];
	tabActive?: TTabItem['key'];
	isSave?: boolean;
}

const SettingColumn = ({
	tableType,
	isSettingCol,
	tabItems,
	mergeMultiKeyCols,
	onSave,
	searchFilterRef,
	...rest
}: TSettingColumn) => {
	if (!isSettingCol) {
		return null;
	}

	const { t } = useTranslation('common');

	const dispatch = useDispatch();

	const selectSettingColumn = useSelector(CommonSelectors.selectSettingColumn);

	const subType = useMemo(
		() => TableSettingColumnSubTypeMap[tableType as TableTypes],
		[tableType]
	);

	const settingColumn = useMemo(() => {
		return selectSettingColumn[subType];
	}, [selectSettingColumn, subType]);

	const [state, setState] = useMergingState<TState>({
		open: false,
		tabItems: [],
		tabActive: tabItems?.[0].key,
		isSave: false,
	});

	const tabItem = useMemo(() => {
		const formattedTabItems = state.tabItems?.find(
			(_) => _.key === state.tabActive
		);
		if (formattedTabItems) {
			formattedTabItems.options = formattedTabItems?.options?.filter?.(
				(option) => option.label !== 'ID'
			);
		}
		return formattedTabItems;
	}, [state.tabItems, state.tabActive]);

	useEffect(() => {
		setState((prev) => ({
			...prev,
			tabItems,
		}));
	}, [tabItems]);

	useEffect(() => {
		dispatch(CommonActions.getSettingColumnRequest({ screenCode: subType }));
	}, [subType]);

	useEffect(() => {
		const setHasMounted = () => {
			dispatch(CommonActions.getSettingColumnRequest({ screenCode: subType }));
		};
		window.addEventListener('popstate', setHasMounted);
		return () => {
			window.removeEventListener('popstate', setHasMounted);
		};
	}, []);

	useEffect(() => {
		const newTabItems = getTabItemsFromSettingColumn(
			tabItems,
			settingColumn?.content
		);

		handleOnSave({
			tabItems: newTabItems,
		});

		setState((prev) => ({
			tabItems: newTabItems,
		}));
	}, [settingColumn, tabItems]);

	const onOpenChange = useCallback((open: boolean) => {
		setState({
			open,
		});
	}, []);

	const onTabItemsChange = useCallback((tabActive: string) => {
		setState({ tabActive });
	}, []);

	const _onChange: GetProp<typeof Checkbox.Group, 'onChange'> = useCallback(
		(checkedValue) => {
			let newTabItem = state.tabItems?.map((tab) => ({
				...tab,
				checkedList:
					tab?.key === state.tabActive
						? (checkedValue as string[])
						: tab.checkedList,
			}));

			setState({
				tabItems: newTabItem,
			});
		},
		[state]
	);

	const handleOnSave = useCallback(
		(stateParams?: Partial<TState>) => {
			const _tabItems = stateParams?.tabItems || state.tabItems;
			const settingListFields: TGetListUserSettingResponse = _tabItems?.map(
				(_) => ({
					objectName: _.key as PageTypeEnum,
					fields: _.checkedList || [],
				})
			);

			onSave?.(settingListFields, state.tabActive);
		},

		[state, onSave]
	);

	const getTabItemsFromSettingColumn = useCallback(
		(
			inputTabItems?: TTabItem[],
			inputSettingColumnList?: TGetListUserSettingResponse
		) => {
			return map(inputTabItems, (tab, index) => {
				const obj = inputSettingColumnList?.find(
					(item) => tab?.key === item.objectName
				);

				const checkedList = getMergedKeysAndRemoveChildKeys({
					fields: obj?.fields,
					mergeMultiKeyCols,
				});

				const options = getSortedCheckedListOrOptions(
					checkedList,
					tab?.options,
					false
				);

				return {
					...tab,
					checkedList,
					options,
				} as TTabItem;
			});
		},
		[mergeMultiKeyCols]
	);

	const handleOnApply = useCallback(() => {
		const getFields = (tab: TTabItem) => {
			const fields = uniq(
				getChildKeysFromMergedKeys({
					fields: [
						...[
							'ID',
							...(isArray(tab?.ids) ? tab.ids.filter((it) => it.trim()) : []),
						],
						...(getSortedCheckedListOrOptions(
							tab.checkedList,
							tab.options
						) as string[]),
					],
					mergeMultiKeyCols,
				}).map((value) =>
					convertCamelToSnakeCaseWithoutTableName(value, tab.key, true)
				) ?? []
			);
			if (tab.sort) {
				return fields.sort(tab.sort);
			}
			return fields;
		};

		const contentMap = state.tabItems?.map((tab) => ({
			objectName: tab.key,
			fields: getFields(tab),
		}));

		const content = JSON.stringify(contentMap);

		dispatch(
			CommonActions.setSettingColumnRequest({
				type: UserSettingTypeEnum.LIST_FIELD_SETTINGS,
				subType,
				content,
				callback: (params) => {
					if (params?.error) return;
					searchFilterRef?.current?.onSearchChange?.();
					dispatch(
						CommonActions.getSettingColumnRequest({ screenCode: subType })
					);
				},
			})
		);

		setState({
			open: false,
		});
	}, [state, mergeMultiKeyCols, subType, searchFilterRef]);

	const handleOnClose = useCallback(() => {
		setState({
			open: false,
		});
	}, []);

	const afterOpenChange = useCallback(
		(visible: boolean) => {
			if (!visible) {
				const newTabItems = getTabItemsFromSettingColumn(
					tabItems,
					settingColumn?.content
				);

				setState({
					tabItems: newTabItems,
				});
			}
		},
		[getTabItemsFromSettingColumn, settingColumn?.content, tabItems]
	);

	// Reorder function when dragging
	const onDragEnd = (result: DropResult, provided: ResponderProvided) => {
		const { source, destination } = result;

		if (!destination || tabItem?.options?.[destination.index]?.fixed) return;

		setState((prev) => {
			const newTabItems = [...prev.tabItems];

			const tabIndex = newTabItems.findIndex(
				(tab) => tab.key === prev.tabActive
			);

			if (tabIndex === -1) return prev;

			const reorderedOptions = [...(newTabItems?.[tabIndex]?.options || [])];

			const [movedOption] = reorderedOptions.splice(source.index, 1);

			reorderedOptions.splice(destination.index, 0, movedOption);

			newTabItems[tabIndex] = {
				...newTabItems[tabIndex],
				options: reorderedOptions,
			};

			return {
				...prev,
				tabItems: newTabItems,
			};
		});
	};

	const content = useCallback(() => {
		return (
			<div className="h-full pb-[60px] space-y-5">
				<div className="h-full overflow-auto">
					<DragDropContext onDragEnd={onDragEnd}>
						<Droppable droppableId="checkbox-group">
							{(provided) => (
								<div {...provided.droppableProps} ref={provided.innerRef}>
									<Checkbox.Group
										className="col gap-y-2"
										value={tabItem?.checkedList}
										onChange={_onChange}
									>
										{tabItem?.options?.length &&
											(
												tabItem?.options as (CheckboxOptionType & {
													fixed: boolean;
												})[]
											)?.map((option, index) => (
												<Draggable
													key={option.value}
													draggableId={option.value}
													index={index}
													isDragDisabled={option?.fixed}
												>
													{(provided) => (
														<div
															ref={provided.innerRef}
															{...provided.draggableProps}
															{...provided.dragHandleProps}
															style={{
																...provided.draggableProps.style,
																cursor: option?.fixed ? 'not-allowed' : 'auto',
																transform: option?.fixed
																	? 'none'
																	: provided.draggableProps.style?.transform,
															}}
														>
															<Checkbox value={option.value}>
																{option.label}
															</Checkbox>
														</div>
													)}
												</Draggable>
											))}
									</Checkbox.Group>
									{provided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
				</div>
				<div className="flex gap-x-2">
					<Button
						className="min-w-min flex-1"
						type="primary"
						onClick={handleOnApply}
					>
						{t?.('apply')}
					</Button>
					<Button
						className="min-w-min flex-1"
						type="primary"
						ghost
						onClick={handleOnClose}
					>
						{t?.('close')}
					</Button>
				</div>
			</div>
		);
	}, [tabItem, onDragEnd, _onChange, handleOnApply, handleOnClose]);

	const title = useCallback(() => {
		return (
			<div className="h-10 content-center mb-3">
				<Tabs
					className="h-10"
					defaultActiveKey={state?.tabActive}
					items={state.tabItems}
					onChange={onTabItemsChange}
				/>
			</div>
		);
	}, [state.tabActive, state.tabItems, onTabItemsChange]);

	return (
		<Popover
			rootClassName="w-[242px]"
			content={content}
			title={title}
			trigger="click"
			placement="bottomRight"
			arrow={false}
			open={state.open}
			onOpenChange={onOpenChange}
			afterOpenChange={afterOpenChange}
		>
			<>
				<Button icon={<SettingOutlined />} iconPosition="start" type="default">
					{t?.('list-settings')}
				</Button>
			</>
		</Popover>
	);
};

export default memo(SettingColumn);

const getMergedKeysAndRemoveChildKeys = ({
	fields = [],
	mergeMultiKeyCols = [],
}: {
	fields?: string[];
	mergeMultiKeyCols?: TSettingColumn['mergeMultiKeyCols'];
}): string[] => {
	if (!fields) return [];
	if (length(fields) < 2 || !mergeMultiKeyCols) return fields;

	let results: string[] = [...fields];

	mergeMultiKeyCols?.forEach((x) => {
		// Find the index of the first childKey in the results array
		const firstChildKeyIndex = results.findIndex((result) =>
			x.childKeys.includes(result)
		);

		// If at least one childKey exists in results
		if (firstChildKeyIndex !== -1) {
			// Remove all childKeys from results
			results = results.filter((result) => !x.childKeys.includes(result));

			// Insert mergedKey at the position of the first childKey found
			results.splice(firstChildKeyIndex, 0, x.mergedKey);
		}
	});

	return results;
};

const getChildKeysFromMergedKeys = ({
	fields = [],
	mergeMultiKeyCols = [],
}: {
	fields: string[];
	mergeMultiKeyCols?: TSettingColumn['mergeMultiKeyCols'];
}): string[] => {
	if (!fields) return [];
	if (length(fields) < 2 || !mergeMultiKeyCols) return fields;

	let results: string[] = [...fields];

	mergeMultiKeyCols?.forEach((x) => {
		const index = results.indexOf(x.mergedKey);

		if (index !== -1) {
			// Remove mergedKey & insert childKeys in that exact spot
			results.splice(index, 1, ...x.childKeys);
		}
	});

	return results;
};

const getSortedCheckedListOrOptions = (
	checkedList: TTabItem['checkedList'],
	options: TTabItem['options'],
	returnValuesOnly = true
): string[] | CheckboxOptionType[] => {
	if (!checkedList || !options) return [];

	const checkedListSet = new Set(checkedList);

	const sortedOptions = clone(options) || [];

	if (returnValuesOnly) {
		return sortedOptions
			?.map((item) => item.value)
			.filter((value) => checkedListSet.has(value));
	} else {
		const optionsFixed = sortedOptions.filter((option) => option.fixed);
		const checkedOptionsNonFixed = sortedOptions.filter(
			(option) => !option.fixed && checkedListSet.has(option.value)
		);

		checkedOptionsNonFixed.sort(
			(a, b) => checkedList.indexOf(a.value) - checkedList.indexOf(b.value)
		);

		const remainingOptionsNonFixed = sortedOptions.filter(
			(option) => !option.fixed && !checkedListSet.has(option.value)
		);

		return [
			...optionsFixed,
			...checkedOptionsNonFixed,
			...remainingOptionsNonFixed,
		];
	}
};
